import { supabase } from '../lib/supabaseClient';
import { compressImageResult } from '../lib/compressImage';
import { ArchivoIlegibleError, archivoIlegible } from '../lib/archivoLegible';
import type { SubmitApplicationInput } from '../domain/applicationSchema';

export type DocumentStatus = 'libre' | 'pendiente' | 'bloqueado' | 'registrado';

/**
 * Traducción del error del trigger de lista negra al mensaje del usuario.
 * El HINT que devuelve la BD dice lo mismo, pero se guardó sin tilde ("esta"),
 * así que el texto correcto vive aquí y no se toma del servidor.
 */
export const MENSAJE_DOCUMENTO_BLOQUEADO = 'Este documento está bloqueado. Contacta a operaciones.';

export class DuplicatePendingError extends Error {
  constructor() {
    super('Ya tienes un registro pendiente con este documento. Espera a que sea revisado.');
    this.name = 'DuplicatePendingError';
  }
}

export class BlockedDocumentError extends Error {
  /**
   * `message` deja usar el texto del trigger de lista negra
   * (`MENSAJE_DOCUMENTO_BLOQUEADO`) en vez del texto por defecto del chequeo previo.
   */
  constructor(message = 'Este documento está bloqueado. Comunícate con el jefe de operaciones.') {
    super(message);
    this.name = 'BlockedDocumentError';
  }
}

export class AlreadyRegisteredError extends Error {
  constructor() {
    super('Este documento ya tiene un usuario creado. Ingresa con tu correo y clave.');
    this.name = 'AlreadyRegisteredError';
  }
}

/** Consulta si el documento puede registrarse (RPC pública, security definer). */
export async function documentStatus(dni: string): Promise<DocumentStatus> {
  const { data, error } = await supabase.rpc('document_registration_status', { p_dni: dni });
  if (error) throw new Error('No se pudo verificar tu documento. Inténtalo de nuevo.');
  return (data as DocumentStatus) ?? 'libre';
}

function assertUsable(status: DocumentStatus): void {
  if (status === 'bloqueado') throw new BlockedDocumentError();
  if (status === 'registrado') throw new AlreadyRegisteredError();
  if (status === 'pendiente') throw new DuplicatePendingError();
}

/**
 * Mensaje de una subida fallida que DICE LA CAUSA en vez de culpar a la conexión.
 *
 * El texto anterior era «Revisa tu conexión» pasara lo que pasara. Eso hizo indiagnosticable el
 * fallo del 2026-09-03: el servidor respondía «permission denied for table profiles» y el
 * postulante leía que su internet estaba mal —con el internet perfecto—. Nadie podía reportar algo
 * útil, y el registro público llevaba roto desde el primer día sin que se notara.
 *
 * La causa real se muestra al usuario a propósito: es fea, pero es lo que le permite mandarnos una
 * captura que sirva. Y va también a la consola, que es donde la busca quien depura.
 */
function mensajeDeSubidaFallida(que: string, error: { message?: string; code?: string }): string {
  const detalle = [error.code, (error.message ?? '').trim()].filter(Boolean).join(' · ');
  // eslint-disable-next-line no-console -- rastro para depurar desde el celular del postulante
  console.error(`Falló ${que}:`, error);
  if (detalle === '') return `No se pudo subir ${que}. Vuelve a intentarlo.`;
  return `No se pudo subir ${que}. El servidor respondió: «${detalle}». `
    + 'Si vuelve a pasar, manda una captura de este mensaje a operaciones.';
}

/** Extensión que le corresponde a un tipo MIME, para no mentir en el nombre del archivo. */
const EXTENSION_POR_TIPO: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
};

/**
 * Comprime si puede, y devuelve el tipo y la extensión **reales** de lo que se va a subir.
 *
 * Antes todo se subía como `image/jpeg` con extensión `.jpg`, hubiera o no conseguido recodificar.
 * Un HEIC de iPhone que el navegador no sabe decodificar se guardaba como `foto.jpg` sin ser un
 * JPEG: el postulante veía «registro enviado» y el revisor abría un archivo roto. El postulante
 * quedaba en la bandeja con una foto que nadie puede mirar, y la culpa parecía suya.
 */
async function archivoParaSubir(
  blob: Blob,
  que: string,
): Promise<{ blob: Blob; contentType: string; ext: string }> {
  /**
   * Se comprueba que el archivo TODAVÍA se pueda leer, antes de intentar subirlo.
   *
   * Sin esto, un `File` de Android que quedó ilegible seguía todo el camino: la compresión fallaba
   * y su `catch` devolvía el original muerto en silencio, y el fallo aparecía recién en el `fetch`
   * —después del preflight— como «Failed to fetch», sin decir nada. Leer un byte acá cuesta nada y
   * convierte un error de red incomprensible en una instrucción: volvé a tomar la foto.
   */
  if (await archivoIlegible(blob)) throw new ArchivoIlegibleError(que);
  const { blob: salida, recodificado } = await compressImageResult(blob);
  if (recodificado) return { blob: salida, contentType: 'image/jpeg', ext: 'jpg' };
  const tipo = (salida.type || '').toLowerCase();
  return {
    blob: salida,
    contentType: tipo || 'application/octet-stream',
    ext: EXTENSION_POR_TIPO[tipo] ?? 'bin',
  };
}

/**
 * Sube una foto del registro público comprimida a
 * `applications/<dni>/<slug>-<marca de tiempo>.jpg`.
 * Slugs usados: `foto`, `documento-frontal`, `documento-posterior`.
 *
 * **La ruta lleva marca de tiempo y la subida NO es `upsert`.** Antes el nombre era fijo
 * (`<slug>.jpg`) y se sobrescribía, lo que rompía el SEGUNDO intento de la misma persona: para
 * sobrescribir, Storage necesita ver el archivo que ya existe, y `anon` no tiene ninguna policy de
 * lectura sobre `applications/` — respondía 403 «new row violates row-level security policy». Es
 * decir: al postulante que se equivocaba de foto y volvía a intentarlo, el sistema no lo dejaba
 * nunca más.
 *
 * Darle lectura a `anon` habría arreglado el reintento abriendo un agujero peor: la ruta lleva el
 * DNI, así que cualquiera podría bajarse la foto del documento de identidad de cualquier postulante
 * probando números. Con nombre único el INSERT siempre es nuevo y no hace falta ningún permiso
 * extra. El costo es que un reintento deja el archivo anterior huérfano — lo mismo que ya hacían
 * los documentos laborales, y es basura barata comparada con filtrar documentos de identidad.
 */
export async function uploadApplicationFile(dni: string, slug: string, blob: Blob): Promise<string> {
  const que = `la imagen (${slug})`;
  const { blob: subir, contentType, ext } = await archivoParaSubir(blob, que);
  const path = `applications/${dni}/${slug}-${Date.now()}.${ext}`;
  const { error } = await subirAStorage(path, subir, contentType, que);
  if (error) throw new Error(mensajeDeSubidaFallida(que, error));
  return path;
}

/** Sube un documento laboral a `documents/<dni>/<type>-<marca de tiempo>.jpg` (ruta del contrato). */
export async function uploadApplicationDocument(
  dni: string,
  type: 'carnet_sanidad' | 'certificado_unico_laboral' | 'otro',
  blob: Blob,
): Promise<string> {
  const { blob: subir, contentType, ext } = await archivoParaSubir(blob, 'el documento');
  const path = `documents/${dni}/${type}-${Date.now()}.${ext}`;
  const { error } = await subirAStorage(path, subir, contentType, 'el documento');
  if (error) throw new Error(mensajeDeSubidaFallida('el documento', error));
  return path;
}

/**
 * La subida a Storage, con lo que se ESCAPABA atrapado.
 *
 * 🔴 `storage-js` no devuelve todos los errores en `{ error }`. Su `catch` termina así:
 *
 *     if (isStorageError(error)) { return { data: null, error } }
 *     throw error
 *
 * Un `TypeError: Failed to fetch` no es un `StorageError`, así que se LANZA — y se saltea el
 * `if (error)` de arriba y todo el trabajo de `mensajeDeSubidaFallida`. Justamente el fallo más
 * común del celular era el único que no llegaba a ningún mensaje: la postulante leía «Failed to
 * fetch» en inglés. Acá se normaliza para que lo lanzado y lo devuelto tomen el mismo camino.
 *
 * El `ArchivoIlegibleError` se deja pasar tal cual: su mensaje ya dice qué hacer y traducirlo a
 * «no se pudo subir» perdería la instrucción, que es la parte útil.
 */
async function subirAStorage(
  path: string,
  blob: Blob,
  contentType: string,
  que: string,
): Promise<{ error: { message?: string; code?: string } | null }> {
  try {
    return await supabase.storage.from('photos').upload(path, blob, { contentType });
  } catch (e) {
    if (e instanceof ArchivoIlegibleError) throw e;
    /**
     * Un fallo de red al SUBIR, con el archivo legible un instante antes, casi siempre es el mismo
     * problema una fracción de segundo después: el puntero al archivo se murió mientras se leía el
     * cuerpo. Se vuelve a comprobar para decidir el mensaje, en vez de adivinar.
     */
    if (await archivoIlegible(blob)) throw new ArchivoIlegibleError(que);
    const msg = e instanceof Error ? e.message : String(e);
    return { error: { message: `${msg} (la petición no llegó a completarse)` } };
  }
}

/** Inserta la solicitud en `worker_applications` (documentos laborales en `documents` jsonb). */
export async function submitApplication(input: SubmitApplicationInput): Promise<void> {
  assertUsable(await documentStatus(input.dni));

  const { error } = await supabase.from('worker_applications').insert({
    document_type: input.documentType,
    dni: input.dni,
    first_names: input.firstNames,
    last_names: input.lastNames,
    phone: input.phone,
    email: input.email,
    birth_date: input.birthDate,
    department: input.department,
    province: input.province,
    district: input.district,
    address: input.address || null,
    bank: input.bank || null,
    account_number: input.accountNumber || null,
    cci: input.cci || null,
    photo_path: input.photoPath,
    doc_front_path: input.docFrontPath,
    doc_back_path: input.docBackPath,
    documents: input.documents,
  });

  if (error) {
    // Lista negra: desde la migración 0005 la aplica el trigger
    // `worker_applications_guard_lista_negra`, que hace `raise exception 'documento_bloqueado'`.
    // PostgREST lo devuelve como {code:'P0001', message:'documento_bloqueado', hint:'…'};
    // sin este caso el postulante bloqueado vería el genérico "revisa tu conexión".
    if (error.code === 'P0001' && (error.message ?? '').includes('documento_bloqueado')) {
      throw new BlockedDocumentError(MENSAJE_DOCUMENTO_BLOQUEADO);
    }
    if (error.code === '23505') throw new DuplicatePendingError();
    /**
     * NO se traduce 42501 a «documento bloqueado». Lo hacía hasta el 2026-09-03 y era una
     * acusación falsa: hasta la 0005 la lista negra vivía en el WITH CHECK de la policy y por eso
     * un bloqueado daba 42501, pero desde entonces el bloqueo llega como P0001 (arriba) y 42501
     * solo puede significar «faltan permisos». Traducirlo hacía que operaciones buscara al
     * postulante en la lista negra, no lo encontrara, y concluyera que mentía — mientras la falla
     * real de permisos no se reportaba en ningún lado. Es el mismo error que tuvo escondido tres
     * semanas el fallo de la subida.
     */
    throw new Error(mensajeDeSubidaFallida('tu registro', error));
  }
}
